export interface HeroProps {
  onBook: () => void;
}

export function Hero({ onBook }: HeroProps) {
  return (
    <section className="hero container">
      <div className="hero-grid">
        <div>
          <div className="hero-eyebrow">
            <span className="dot" />
            <span>LMcad</span>
          </div>

          <h1 className="hero-title">
            Stop modeling
            <br />
            the obvious. <em>Start with a draft.</em>
          </h1>

          <p className="hero-sub">
            Describe the part you want. A hex bolt, a planetary reducer, a
            swept wing. You get a parametric model, a 3D preview, and a clean{' '}
            <strong>STEP</strong> file ready to open in{' '}
            <strong>CATIA</strong>, <strong>Creo</strong>, or{' '}
            <strong>SolidWorks</strong>. The boring start, done.
          </p>

          <div className="hero-actions">
            <button type="button" className="cta cta-primary" onClick={onBook}>
              Book a demo
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path
                  d="M2 7 H12 M8 3 L12 7 L8 11"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <a className="cta cta-ghost" href="#how">
              How it works
            </a>
          </div>

          <div className="hero-meta">
            <div className="hero-meta-item">Parametric, not text-to-mesh</div>
            <div className="hero-meta-item">Exports to STEP</div>
          </div>
        </div>

        <HeroMock />
      </div>
    </section>
  );
}

function HeroMock() {
  return (
    <div className="hero-mock">
      <div className="hero-mock-chrome">
        <span className="dot3" />
        <span className="dot3" style={{ background: 'var(--line)' }} />
        <span className="dot3" style={{ background: 'var(--line)' }} />
        <span className="label">PROJ-001 · M8 HEX BOLT</span>
      </div>
      <div className="hero-mock-body">
        <div className="hero-mock-pane" aria-hidden>
          <h6>Chat</h6>
          <div className="hero-mock-bubble user">
            Make a 50mm M8 hex bolt with a 30mm threaded section, head AF 13mm.
          </div>
          <div className="hero-mock-bubble">
            Drafted. Threads ISO M8×1.25, head per DIN&nbsp;933. Parameters on
            the right.
          </div>
          <div
            className="hero-mock-bubble"
            style={{
              fontStyle: 'italic',
              color: 'var(--fg-muted)',
              fontFamily: 'var(--font-serif)',
              fontSize: 12,
              borderStyle: 'dashed',
            }}
          >
            <span style={{ color: 'var(--accent)' }}>·</span> Ready. Tweak or
            export STEP.
          </div>
        </div>

        <div className="hero-mock-pane" aria-hidden>
          <h6>Viewer</h6>
          <div className="hero-mock-viewer">
            <BoltSVG />
          </div>
        </div>

        <div className="hero-mock-pane" aria-hidden>
          <h6>Parameters</h6>
          <div>
            <div className="hero-mock-row">
              <span className="name">length</span>
              <span className="val">
                50<span className="unit">mm</span>
              </span>
            </div>
            <div className="hero-mock-slider" />
            <div className="hero-mock-row">
              <span className="name">thread_len</span>
              <span className="val">
                30<span className="unit">mm</span>
              </span>
            </div>
            <div className="hero-mock-slider" />
            <div className="hero-mock-row">
              <span className="name">head_af</span>
              <span className="val">
                13<span className="unit">mm</span>
              </span>
            </div>
            <div className="hero-mock-slider" />
            <div className="hero-mock-row">
              <span className="name">pitch</span>
              <span className="val">
                1.25<span className="unit">mm</span>
              </span>
            </div>
            <div className="hero-mock-row">
              <span className="name">material</span>
              <span className="val">A2-70</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Stylized iso-projection of a hex bolt — drawn in SVG so it inherits
 * theme colors and stays crisp at any size. Not a real WebGL viewer
 * (we don't want users expecting interactive 3D on the marketing page).
 */
function BoltSVG() {
  return (
    <svg viewBox="0 0 240 200" fill="none">
      {/* shadow */}
      <ellipse cx="120" cy="180" rx="80" ry="6" fill="var(--fg)" opacity="0.06" />
      {/* shaft */}
      <path
        d="M70 50 L120 30 L170 50 L170 130 L120 150 L70 130 Z"
        fill="var(--bg-elev)"
        stroke="var(--fg-muted)"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
      <path
        d="M70 50 L120 70 L170 50"
        stroke="var(--fg-muted)"
        strokeWidth="1.2"
        strokeLinejoin="round"
        fill="none"
      />
      <path
        d="M120 70 L120 150"
        stroke="var(--fg-muted)"
        strokeWidth="1.2"
        fill="none"
      />
      {/* threads — diagonal hatching on the lower 60% */}
      {Array.from({ length: 8 }).map((_, i) => {
        const t = 80 + i * 8;
        return (
          <g key={i}>
            <path
              d={`M${72 + i * 0.6} ${t} L${118 - i * 0.6} ${t + 18}`}
              stroke="var(--fg-faint)"
              strokeWidth="0.8"
            />
            <path
              d={`M${168 - i * 0.6} ${t} L${122 + i * 0.6} ${t + 18}`}
              stroke="var(--fg-faint)"
              strokeWidth="0.8"
            />
          </g>
        );
      })}
      {/* head highlight */}
      <path
        d="M120 30 L170 50 L120 70 Z"
        fill="var(--accent)"
        opacity="0.08"
      />

      {/* dimension annotation — just enough to show this is a parametric part */}
      <g
        fontFamily="var(--font-mono)"
        fontSize="9"
        fill="var(--accent)"
        letterSpacing="0.12em"
      >
        <line x1="190" y1="50" x2="200" y2="50" stroke="var(--accent)" strokeWidth="0.8" />
        <line x1="190" y1="150" x2="200" y2="150" stroke="var(--accent)" strokeWidth="0.8" />
        <line x1="200" y1="50" x2="200" y2="150" stroke="var(--accent)" strokeWidth="0.8" />
        <text x="206" y="103">L = 50</text>
      </g>
    </svg>
  );
}
